<template>
  <div class="fullWidth">
    <ProductCarousel :product="product" />

    <div class="productHeader row alignCentreRow headerRow">
      <div class="headercontainer column fullWidth">
        <div class="row alignCentreRow justifyToStartEnd">
          <h3 class="smallmargintopbottom">
            {{ productData.itemName || productData.serviceName }}
          </h3>
          <div class="leftGroup" v-if="productData.price">
            <div
              v-if="productData.existingStock !== undefined"
              class="product-stock"
            >
              <small
                class="soldout gray"
                v-if="productData.existingStock === 0"
              >
                Sold
              </small>
              <small
                v-else
                :class="{ stockWarningColor: productData.existingStock < 2 }"
              >
                {{ productData.existingStock }} in stock
              </small>
            </div>
            <div class="price innerSticker smallClickableHeight">
              <div class="row alignCentreRow justifyToCentre noWrap">
                <span class="priceNumberSymbol">
                  {{ parseFloat(productData.price).toFixed(2) }}
                  {{ coinSymbol }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- <p>
          {{ productData.itemDescription || productData.serviceDescription }}
        </p> -->
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from "vue";
import { auth } from "@/firebase";
import { useStore } from "vuex";
import ProductCarousel from "@/components/ProductComponents/ProductCarousel.vue";
import UserProfileImage from "@/components/ProductComponents/UserProfileImage.vue";

export default {
  name: "PreviewProduct",
  components: {
    ProductCarousel,
    UserProfileImage,
  },
  props: {
    productData: {
      type: Object,
      required: true,
    },
    userUnitsOrg: {
      type: Object,
      required: true,
    },
    coinSymbol: {
      type: String,
      required: true,
    },
    thumbnailIndex: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const store = useStore();

    const currentUser = ref(auth.currentUser);
    const userCredentials = computed(() => store.getters.userCredentials);

    auth.onAuthStateChanged((userCredentials) => {
      store.dispatch("fetchUserCredentials", userCredentials);
    });

    const imageDataUrls = ref([]);

    const product = computed(() => ({
      ...props.productData,
      images: imageDataUrls.value,
    }));

    const convertFilesToDataUrls = () => {
      console.log(
        "Converting files to data URLs in PreviewItem",
        props.productData
      );
      imageDataUrls.value = [];

      // Check for imagePreviews (from new product) or images (from existing product)
      const imageSource =
        props.productData.imagePreviews || props.productData.images || [];
      // If imagePreviews exists, it's already in UI order (thumbnail-first). Don't reorder again.
      const isPreordered =
        Array.isArray(props.productData.imagePreviews) &&
        props.productData.imagePreviews.length > 0;

      if (imageSource && imageSource.length > 0) {
        // Create a copy of the array to avoid modifying the original
        const images = Array.isArray(imageSource)
          ? [...imageSource]
          : [imageSource];
        console.log("Processing images for preview:", images);

        const thumbnailIndex = props.thumbnailIndex;

        // Move thumbnail to front only when using raw images (not pre-ordered imagePreviews)
        if (
          !isPreordered &&
          thumbnailIndex >= 0 &&
          thumbnailIndex < images.length
        ) {
          const thumbnailImage = images.splice(thumbnailIndex, 1)[0];
          images.unshift(thumbnailImage); // Move the thumbnail to the first position
        }

        // Process each image
        images.forEach((file) => {
          if (typeof file === "string") {
            // For URL strings, just use them directly
            imageDataUrls.value.push(file);
          } else if (file instanceof File) {
            // For File objects, create a data URL
            const reader = new FileReader();
            reader.onload = (e) => {
              imageDataUrls.value.push(e.target.result);
            };
            reader.readAsDataURL(file);
          } else if (file instanceof Blob) {
            // For Blob objects (including those from URL.createObjectURL)
            imageDataUrls.value.push(URL.createObjectURL(file));
          }
        });

        console.log("Processed image data URLs:", imageDataUrls.value);
      }
    };

    const fieldConfig = {
      itemName: "Product name",
      itemDescription: "Description",
      existingStock: "Existing stock",
      price: "Price per Item",
      selectedCategory: "Category",
      selectedCondition: "Condition",
    };

    const tableRows = computed(() => {
      if (props.productData) {
        const entries = Object.entries(props.productData);
        return entries
          .filter(([key]) => key !== "imagePreviews")
          .map(([key, value]) => {
            let displayValue = value;
            if (key === "price") {
              // Fixed template literal usage
              displayValue = `${value} ${props.coinSymbol}`;
            }
            return {
              col1: fieldConfig[key] || key,
              col2: displayValue,
            };
          });
      }
    });

    // Watch both imagePreviews and images to ensure we catch updates from either source
    watch(
      [() => props.productData.imagePreviews, () => props.productData.images],
      () => {
        console.log("Image data changed, converting to data URLs");
        convertFilesToDataUrls();
      },
      { immediate: true, deep: true }
    );

    // Also watch the entire productData object
    watch(
      () => props.productData,
      () => {
        console.log("Product data changed, converting to data URLs");
        convertFilesToDataUrls();
      },
      { immediate: true, deep: true }
    );

    const confirmAndUpload = () => {
      emit("confirmUpload");
    };

    return {
      confirmAndUpload,
      imageDataUrls,
      tableRows,
      product,
      currentUser,
    };
  },
};
</script>

<style scoped>
.productCardComplete {
  display: flex;
  flex-direction: column; /* Make it a column */
  align-items: center; /* Align items vertically, not horizontally */
  padding: var(--space-5);
  border-radius: var();
}

.allImgPreviewCont {
  column-count: 3; /* Adjust the number of columns */
  column-gap: var(--space-5); /* Gap between columns */
}

.previewImageContainer {
  display: inline-block;
  margin: 0 0 var(--space-4);
  width: 100%;
}

.previewImage {
  width: 100%;
  border: 0px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}
.smallText {
  text-align: left;
}
.headercontainer {
  margin-top: var(--space-5);
}
.leftGroup {
  display: flex;
  flex-direction: row;
  gap: var(--space-5);
  align-items: center;
}
.price {
  width: fit-content;
  border-radius: var(--radius-pill);
  background-color: var(--color-surface-raised);
}

@media (max-width: 590px) {
  .allImgPreviewCont {
    column-count: 3;
  }
}
</style>
